import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Moon, Repeat, Sun, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';
import { useTheme } from '@/lib/theme';
import { useAuthStore } from '@/store/auth.store';
import { Logo } from '@/components/ui/Logo';

/*
  ─────────────────────────────────────────────────────────────────────────────
  The shell for the two field apps — parent and gate.

  Deliberately NOT the admin shell. Those screens are a dense desktop dashboard
  with a 176px rail; these two are phone experiences that happen to be served in
  a browser. A parent opens this on a phone in a car park; a gate officer holds
  it one-handed at a gate in the sun. So: no sidebar, a single centred column
  with a comfortable maximum width, larger touch targets, and the navigation as
  a bottom bar within thumb reach rather than a side rail.

  It keeps the command navy bar and every token from the design system, so the
  three audiences are visibly one product.
  ─────────────────────────────────────────────────────────────────────────────
*/

export interface FieldTab {
  label: string;
  to: string;
  icon: LucideIcon;
  end?: boolean;
}

/*
  ── Why one tab can open a sheet ────────────────────────────────────────────

  A bottom bar is thumb real estate, and it was holding seven destinations. At
  phone width that is about 53px each, which is where a nine-character label
  starts to truncate — and more to the point it gave a page a parent opens once
  a year the same prominence as the pickup code they open every afternoon.

  So the bar holds what is used often and a `More` tab holds the rest. Nothing
  is removed and no URL changes; the rarely-used pages are one extra tap away
  instead of competing for the same space.
*/
export interface FieldMoreTab {
  label: string;
  icon: LucideIcon;
  items: Array<{ label: string; to: string; icon: LucideIcon; hint?: string }>;
}

export type FieldBarItem = FieldTab | FieldMoreTab;

const isMore = (item: FieldBarItem): item is FieldMoreTab => 'items' in item;

export function FieldLayout({
  appLabel,
  width = 'narrow',
  tabs,
  children,
}: {
  /** Shown beside the mark so it is never ambiguous which app you are in — it
   *  matters when a staff member has both open. */
  appLabel: string;
  /** `narrow` for the parent app (a phone column), `wide` for the gate app,
   *  which shows a photo and a collector list side by side on a tablet. */
  width?: 'narrow' | 'wide';
  tabs?: FieldBarItem[];
  children: ReactNode;
}) {
  const { user, logout } = useAuthStore();
  const { resolved, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  /* Navigating away closes it — otherwise tapping a row inside the sheet
     leaves the sheet sitting over the page it just opened. */
  useEffect(() => setMoreOpen(false), [location.pathname]);

  const moreTab = tabs?.find(isMore);

  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMoreOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const signOut = async (to = '/login') => {
    await logout();
    navigate(to, { replace: true });
  };

  const initial = user?.fullName?.[0]?.toUpperCase() ?? user?.email?.[0]?.toUpperCase() ?? '·';

  return (
    <div className="min-h-screen bg-background">
      <header className="fixed inset-x-0 top-0 z-50 h-bar bg-navbar text-navbar-foreground">
        <div
          className={cn(
            'mx-auto flex h-full items-center justify-between px-3',
            width === 'narrow' ? 'max-w-[520px]' : 'max-w-[760px]',
          )}
        >
          <Link
            to="/"
            className="flex min-w-0 items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navbar-foreground/50"
          >
            <Logo size={22} tone="reversed" className="flex-shrink-0" />
            <span className="truncate text-[12.5px] font-bold tracking-tight">SafePass</span>
            <span className="flex-shrink-0 rounded-sm border border-navbar-foreground/25 px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-navbar-foreground/80">
              {appLabel}
            </span>
          </Link>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={toggle}
              aria-label={resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              className="flex h-7 w-7 items-center justify-center rounded-md text-navbar-foreground/70 transition-colors hover:bg-navbar-foreground/10 hover:text-navbar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navbar-foreground/50"
            >
              {resolved === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="flex h-7 items-center gap-1 rounded-md pl-1 pr-1.5 transition-colors hover:bg-navbar-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navbar-foreground/50"
              >
                <span className="flex h-[22px] w-[22px] items-center justify-center rounded-md bg-navbar-foreground/15 text-[10.5px] font-bold">
                  {initial}
                </span>
                <ChevronDown size={12} className="text-navbar-foreground/60" />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-[calc(100%+6px)] w-60 overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-overlay animate-slide-up"
                >
                  <div className="border-b border-border px-3 py-2.5">
                    <p className="truncate text-[12px] font-bold">
                      {user?.fullName ?? user?.email ?? 'Signed in'}
                    </p>
                    {user?.role && (
                      <p className={cn(styles.label, 'mt-1')}>{user.role.replace(/_/g, ' ')}</p>
                    )}
                  </div>

                  {/*
                    Switching apps signs you out, and says so. It has to: a
                    parent's token is not authorised for the gate endpoints and a
                    gate officer's is not authorised for a parent's children, so
                    carrying the session across would just produce 403s on the
                    first screen.
                  */}
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => void signOut('/')}
                    className="flex w-full items-center gap-2 border-b border-border px-3 py-2.5 text-left text-[11.5px] font-semibold text-foreground transition-colors hover:bg-muted"
                  >
                    <Repeat size={13} className="text-muted-foreground" />
                    <span>
                      Switch app
                      <span className="ml-1 font-normal text-muted-foreground">— signs you out</span>
                    </span>
                  </button>

                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => void signOut('/login')}
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[11.5px] font-semibold text-[hsl(var(--record-ink-danger))] transition-colors hover:bg-destructive/10"
                  >
                    <LogOut size={13} />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main
        className={cn(
          'mx-auto px-3 pt-[calc(theme(spacing.bar)+0.75rem)]',
          width === 'narrow' ? 'max-w-[520px]' : 'max-w-[760px]',
          /* Clears the fixed bottom bar so the last card is never trapped under it. */
          tabs ? 'pb-[76px]' : 'pb-5',
        )}
      >
        {children}
      </main>

      {tabs && (
        <nav
          aria-label="Sections"
          className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card"
          /* Keeps the bar above the iOS home indicator in Safari. */
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div
            className={cn(
              'mx-auto flex',
              width === 'narrow' ? 'max-w-[520px]' : 'max-w-[760px]',
            )}
          >
            {tabs.map((item) => {
              if (!isMore(item)) {
                const { label, to, icon: Icon, end } = item;
                return (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                      cn(
                        'flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-bold uppercase tracking-[0.06em] transition-colors',
                        isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon size={18} strokeWidth={isActive ? 2.3 : 1.8} />
                        {label}
                      </>
                    )}
                  </NavLink>
                );
              }

              /* `More` lights up when one of the pages behind it is open, so a
                 parent reading their pickup history can still see where in the
                 app they are. */
              const Icon = item.icon;
              const insideMore = item.items.some((i) => location.pathname.startsWith(i.to));
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => setMoreOpen((v) => !v)}
                  aria-expanded={moreOpen}
                  aria-haspopup="menu"
                  className={cn(
                    'flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-bold uppercase tracking-[0.06em] transition-colors',
                    insideMore || moreOpen ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icon size={18} strokeWidth={insideMore || moreOpen ? 2.3 : 1.8} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {/* ── The More sheet ─────────────────────────────────────────────── */}
      {moreTab && moreOpen && (
        <>
          <div
            className="fixed inset-0 z-[55] bg-black/40 animate-fade-in"
            onClick={() => setMoreOpen(false)}
            aria-hidden
          />
          <div
            role="menu"
            aria-label={moreTab.label}
            className={cn(
              'fixed inset-x-0 z-[56] mx-auto overflow-clip rounded-t-xl border border-border bg-card shadow-overlay animate-slide-up',
              width === 'narrow' ? 'max-w-[520px]' : 'max-w-[760px]',
            )}
            /* Sits on top of the bar rather than above it: the bar is still
               the thing the thumb is near, and a gap between the two reads as
               a misaligned overlay. */
            style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 61px)' }}
          >
            {moreTab.items.map(({ label, to, icon: ItemIcon, hint }) => (
              <NavLink
                key={to}
                to={to}
                role="menuitem"
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 border-b border-border px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-muted',
                    isActive && 'bg-muted',
                  )
                }
              >
                <ItemIcon size={17} className="flex-shrink-0 text-muted-foreground" />
                <span className="min-w-0">
                  <span className="block text-[12.5px] font-bold text-foreground">{label}</span>
                  {hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}
                </span>
              </NavLink>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
